//* Libraries imports
import { useMemo } from "react";
import { motion } from "framer-motion";
import "./essay-feedback.scss";

//* Types imports
import { Correction } from "../../../../schemas/common/essay/essay";

interface EssayFeedbackProps {
  corrections: Correction[];
  isVisible: boolean;
  onClose: () => void;
}

export function EssayFeedback({ corrections, isVisible, onClose }: EssayFeedbackProps) {  
  const allAnnotations = useMemo(() => {
    return corrections.flatMap(correction => 
      correction.annotations?.map((annotation, index) => ({
        ...annotation,
        correctionId: correction.id,
        displayIndex: index + 1,
      })) || []
    );
  }, [corrections]);

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!corrections || corrections.length === 0) {
    return null; // Don't render anything when no corrections
  }

  return (
    <motion.div 
      className="essay-feedback-sidebar"
      initial={{ opacity: 0, x: 300, scale: 0.95 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        x: isVisible ? 0 : 300,
        scale: isVisible ? 1 : 0.95
      }}
      exit={{ opacity: 0, x: 300, scale: 0.95 }}
      transition={{ 
        duration: 0.5, 
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.3 },
        scale: { duration: 0.4 }
      }}
      style={{ 
        pointerEvents: isVisible ? 'auto' : 'none',
        visibility: isVisible ? 'visible' : 'hidden'
      }}
    >      {/* Fixed Header */}
      <motion.div 
        className="essay-feedback-header p-3 d-flex align-items-center justify-content-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <motion.h2 
            className="essay-feedback-title mb-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            Feedback
          </motion.h2>
          {allAnnotations.length > 0 && (
            <motion.p 
              className="essay-feedback-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              {allAnnotations.length} {allAnnotations.length === 1 ? 'comentário' : 'comentários'}
            </motion.p>
          )}        </motion.div>
        
        <motion.button 
          className="close-sidebar-btn"
          onClick={onClose}
          aria-label="Fechar sidebar de feedback"
          title="Fechar sidebar"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </motion.button>
      </motion.div>      {/* Scrollable Content */}
      <motion.div 
        className="essay-feedback-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {allAnnotations.length > 0 ? (
          <motion.div 
            className="comments-container p-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {allAnnotations.map((annotation, index) => (
              <motion.div 
                key={`${annotation.correctionId}-${annotation.id}`} 
                className="comment-item p-3 mb-3"
                initial={{ opacity: 0, x: -50, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.5 + (index * 0.1),
                  ease: [0.4, 0, 0.2, 1]
                }}
                whileHover={{ 
                  x: 8, 
                  transition: { duration: 0.2 } 
                }}
              >
                {/* Comment Author */}
                <div className="comment-author">
                  <div className="comment-avatar">
                    {getInitials(annotation.correctorName)}
                  </div>
                  
                  <div className="comment-author-info">
                    <h4 className="comment-author-name">
                      {annotation.correctorName}
                    </h4>
                    <p className="comment-author-role">
                      Corretor
                    </p>
                  </div>
                  
                  <div className="comment-badge">
                    #{annotation.displayIndex}
                  </div>
                </div>
                
                {/* Comment Content */}
                <div className="comment-content">
                  {annotation.comentario}
                </div>              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            className="empty-state"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <div className="empty-icon d-flex align-items-center justify-content-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
              </svg>
            </div>
            <h4 className="empty-title">Nenhum comentário</h4>
            <p className="empty-description">
              Esta redação ainda não possui comentários dos corretores.            </p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
